import {
    LoadingWrapper,
    MainPageContent,
    ResponsiveHStack,
    SideNavSection,
    TabsSection,
} from "./PageWrapper.styles";

import DashboardSideNav from "shared/components/data-display/dashboards/DashboardSideNav";
import Footer from "shared/components/layout/footer/Footer";
import LinkTabs from "shared/components/navigation/tabs/LinkTabs";
import Loading from "shared/components/feedback/loading/Loading";
import PageHead from "shared/components/layout/heads/PageHead";
import PropTypes from "prop-types";
import SessionContext from "app/contexts/SessionContext";
import Unauthorized from "app/pages/Unauthorized";
import { useContext } from "react";
import { useLocation } from "react-router-dom";
import { usePageAccess } from "shared/hooks/access/usePageAccess.js";

export { LoadingWrapper } from "./PageWrapper.styles";

const PageWrapper = ({
    menuOptions,
    tabs,
    children,
    loading = false,
    customAuthPath,
    listOptions,
    isAlreadyAuthorize,
    displayFooter = true,
    minHeight = null,
    paddingTop = null,
    paddingBottom = null,
    breadcrumbMarginBottom = null,
    dashboardSideNav = false,
    tabsMarginBottom = null,
    displayUnauthorizedMsg = true,
    positionOfWrapper = null,
    compact = true,
    backgroundAuth = false,
    ...rest
}) => {
    const { pathname } = useLocation();
    const session = useContext(SessionContext);

    const { authorized } = usePageAccess(
        isAlreadyAuthorize ? null : (customAuthPath || pathname),
        session?.legacyId,
        session?.currentDepartmentId
    );

    const authIsLoading = !isAlreadyAuthorize && authorized === null;

    if (!backgroundAuth && (authIsLoading || loading)) {
        return (
            <LoadingWrapper>
                <Loading text="Loading Page" />
            </LoadingWrapper>
        );
    }

    if (!isAlreadyAuthorize && !authIsLoading && !authorized) {

        if (!displayUnauthorizedMsg) {
            return <></>; // this is only used in retainers dashboard. I'm not sure why.
        }
        
        return <Unauthorized />;
    }

    const renderTabsAndChildren = () => {
        return (
            <>
                {tabs && displayFooter && (
                    <LinkTabs
                        tabs={tabs}
                        style={{
                            marginBottom: tabsMarginBottom ?? "32px",
                        }}
                    />
                )}
                <div>{children}</div>
            </>
        );
    };

    return (
        <>
            <MainPageContent
                minHeight={minHeight}
                paddingTop={paddingTop}
                paddingBottom={paddingBottom}
                positionOfWrapper={positionOfWrapper}
            >
                <PageHead
                    style={{
                        marginTop: "24px",
                        marginBottom: breadcrumbMarginBottom ?? "16px",
                    }}
                    menuOptions={menuOptions}
                    listOptions={listOptions}
                    compact={compact}
                    {...rest}
                />
                {dashboardSideNav ? (
                    <ResponsiveHStack spacing="Two">
                        {dashboardSideNav && (
                            <SideNavSection className="sideNav">
                                <DashboardSideNav
                                    sideNavTabs={dashboardSideNav}
                                />
                            </SideNavSection>
                        )}
                        <TabsSection className="linkTabs">
                            {renderTabsAndChildren()}
                        </TabsSection>
                    </ResponsiveHStack>
                ) : (
                    renderTabsAndChildren()
                )}
            </MainPageContent>
            {displayFooter && <Footer />}
        </>
    );
};

PageWrapper.propTypes = {
    menuOptions: PropTypes.array,
    tabs: PropTypes.array,
    children: PropTypes.node,
    loading: PropTypes.bool,
    customAuthPath: PropTypes.string,
    listOptions: PropTypes.array,
    isAlreadyAuthorize: PropTypes.bool,
    displayFooter: PropTypes.bool,
    minHeight: PropTypes.string,
    paddingTop: PropTypes.string,
    paddingBottom: PropTypes.string,
    breadcrumbMarginBottom: PropTypes.string,
    dashboardSideNav: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
    tabsMarginBottom: PropTypes.string,
    displayUnauthorizedMsg: PropTypes.bool,
    positionOfWrapper: PropTypes.string,
    compact: PropTypes.bool,
    backgroundAuth: PropTypes.bool,
};

export default PageWrapper;
